import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"

import { queryKeys } from "~/api/cache"
import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import useConfirmSolarProposalSummary from "~/api/hooks/useConfirmSolarProposalSummary"
import useRecalculateSolarProposalSavings from "~/api/hooks/useRecalculateSolarProposalSavings"
import { solarProposalService } from "~/api/services/solar-proposal.service"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { solarPricingErrorMessage } from "../solar-pricing-error"
import { describeRecalculateError } from "../solar-updates"

export type SolarSummaryConfirmStage = `confirming` | `pricing` | `savings`

/** V1's loading modal named each stage; so does this one. */
export const SUMMARY_CONFIRM_STAGE_LABELS: Record<
  SolarSummaryConfirmStage,
  string
> = {
  pricing: `Calculating pricing…`,
  savings: `Calculating savings…`,
  confirming: `Opening proposal…`,
}

export const CONFIRM_AND_OPEN_LABEL = `Confirm & open proposal`
export const SUMMARY_CONFIRM_FAILED = `The proposal didn't open. Your Summary is saved, so you can try again.`

/** A calculation that answered but not as current. */
class NotCurrentError extends Error {
  public constructor(public readonly status: string) {
    super(status)
  }
}

export type SolarSummaryConfirmFailure = {
  stage: SolarSummaryConfirmStage
  /** Seller-facing, from the copy each calculation already uses. */
  message: string
}

const describeFailure = (
  stage: SolarSummaryConfirmStage,
  error: unknown,
): string => {
  if (error instanceof NotCurrentError) {
    return `${stage === `pricing` ? `Pricing` : `Savings`} came back ${error.status.replace(/_/g, ` `)} rather than current. Try again.`
  }
  switch (stage) {
    case `pricing`:
      // A Credit Human loan-term refusal says what to fix.
      return solarPricingErrorMessage(error)
    case `savings`:
      return describeRecalculateError(error)
    case `confirming`:
      // A 400 names every requirement still missing.
      return parseAxiosError(error, SUMMARY_CONFIRM_FAILED)
  }
}

/**
 * Confirm & open proposal: Refresh Pricing, then Recalculate Savings, each
 * skipped when its stored result is already current, then the server-checked
 * confirm (Battery's initial-setup pattern). Freshness is read again first,
 * since a save a moment ago may have left it out of date. Any failure stops
 * the run and keeps the seller on the Summary; a confirm opens the proposal.
 */
export const useSolarSummaryConfirm = (
  proposalId: string,
  /** Where the proposal opens: the link that met the Summary, else the proposal. */
  returnTo: string,
): {
  run: () => Promise<void>
  /** Clears a failure the seller has moved on from. */
  reset: () => void
  stage: SolarSummaryConfirmStage | null
  failure: SolarSummaryConfirmFailure | null
} => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const calculatePricing = useCalculateSolarProposalPricing()
  const recalculateSavings = useRecalculateSolarProposalSavings()
  const confirm = useConfirmSolarProposalSummary()
  const [stage, setStage] = useState<SolarSummaryConfirmStage | null>(null)
  const [failure, setFailure] = useState<SolarSummaryConfirmFailure | null>(null)

  const run = async (): Promise<void> => {
    let current: SolarSummaryConfirmStage = `pricing`
    setFailure(null)
    try {
      setStage(current)
      const pricing = await queryClient
        .fetchQuery({
          queryKey: queryKeys.solarProposal.pricing(proposalId),
          queryFn: () => solarProposalService.getPricing(proposalId),
          staleTime: 0,
        })
        .catch(() => null)
      if (pricing?.pricingStatus !== `current`) {
        const priced = await calculatePricing.mutateAsync({ proposalId })
        if (priced.pricingStatus !== `current`) {
          throw new NotCurrentError(priced.pricingStatus)
        }
      }

      current = `savings`
      setStage(current)
      const savings = await queryClient
        .fetchQuery({
          queryKey: queryKeys.solarProposal.savings(proposalId),
          queryFn: () => solarProposalService.getSavings(proposalId),
          staleTime: 0,
        })
        .catch(() => null)
      if (savings?.savingsStatus !== `current`) {
        const saved = await recalculateSavings.mutateAsync({ proposalId })
        if (saved.savingsStatus !== `current`) {
          throw new NotCurrentError(saved.savingsStatus)
        }
      }

      current = `confirming`
      setStage(current)
      await confirm.mutateAsync({ proposalId })
      setStage(null)
      navigate(returnTo, { replace: true })
    } catch (error) {
      if (current === `confirming`) {
        // A 400 names what's missing; the links come from the readiness read,
        // so it's read again before the failure shows.
        await queryClient
          .fetchQuery({
            queryKey: queryKeys.solarProposal.summary(proposalId),
            queryFn: () => solarProposalService.getSummary(proposalId),
            staleTime: 0,
          })
          .catch(() => null)
      }
      setStage(null)
      setFailure({ stage: current, message: describeFailure(current, error) })
    }
  }

  return { run, reset: () => setFailure(null), stage, failure }
}
